class SensorService {
  constructor() {
    this.port = null;
    this.reader = null;
    this.isOpen = false;
    this.baudRate = 115200;
    this.isStopping = false;
    this.readQueue = Promise.resolve();
    this.isReading = false;
    console.log('SensorService initialized');
  }

  async delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async openPort() {
    console.log('Attempting to open port...');
    if (!this.port) {
      throw new Error('No port selected');
    }

    for (let attempt = 1; attempt <= 5; attempt++) {
      try {
        console.log(`Open port attempt ${attempt}/5`);
        
        // Force close any existing connections
        await this.forceClosePort();
        
        // Attempt to open the port
        await this.port.open({ baudRate: this.baudRate });
        
        // Wait for the port to be fully opened
        await this.delay(1000);
        
        if (this.port.readable && this.port.writable) {
          this.reader = this.port.readable.getReader();
          this.writer = null; // We'll get the writer when we need it
          this.isOpen = true;
          this.writeQueue = []; // Reset the write queue
          this.isWriting = false;
          console.log('Port opened successfully');
          return;
        } else {
          throw new Error('Port opened but not readable/writable');
        }
      } catch (error) {
        console.error(`Failed to open port (Attempt ${attempt}/5):`, error);
        if (attempt === 5) {
          throw error;
        }
        await this.delay(1000); // Wait before retrying
      }
    }
  }

  async forceClosePort() {
    console.log('Forcibly closing port and releasing resources...');
    
    if (this.reader) {
      try {
        await this.reader.cancel();
      } catch (error) {
        console.warn('Error cancelling reader:', error);
      }
      try {
        this.reader.releaseLock();
      } catch (error) {
        console.warn('Error releasing reader lock:', error);
      }
      this.reader = null;
    }
    
    if (this.writer) {
      try {
        await this.writer.close();
      } catch (error) {
        console.warn('Error closing writer:', error);
      }
      try {
        this.writer.releaseLock();
      } catch (error) {
        console.warn('Error releasing writer lock:', error);
      }
      this.writer = null;
    }
    
    if (this.port) {
      try {
        await this.port.close();
      } catch (error) {
        console.warn('Error closing port:', error);
      }
    }
    
    this.isOpen = false;
    this.writeQueue = []; // Clear the write queue
    this.isWriting = false;
    console.log('Port forcibly closed and resources released');
  }

  async closePort() {
    if (this.isStopping) return;
    this.isStopping = true;

    try {
      await this.forceClosePort();
    } catch (error) {
      console.error('Error in closePort:', error);
    } finally {
      this.isStopping = false;
    }
  }

  async writeData(data) {
    return new Promise((resolve, reject) => {
      if (!this.writeQueue) {
        this.writeQueue = [];
      }
      this.writeQueue.push({ data, resolve, reject });
      this.processWriteQueue();
    });
  }

  isPortOpen() {
    return this.isOpen;
  }

  async processWriteQueue() {
    if (this.isWriting || !this.writeQueue || this.writeQueue.length === 0) {
      return;
    }

    this.isWriting = true;

    const { data, resolve, reject } = this.writeQueue.shift();

    try {
      if (!this.isOpen) {
        throw new Error('Port is not open');
      }

      if (!this.writer) {
        this.writer = this.port.writable.getWriter();
      }

      await this.writer.write(data);
      console.log('Data written to port:', new TextDecoder().decode(data));
      resolve();
    } catch (error) {
      console.error('Error writing data:', error);
      reject(error);
    } finally {
      this.isWriting = false;
      this.processWriteQueue();
    }
  }

  async clearStream() {
    if (this.reader) {
      await this.reader.cancel(); // Cancels the reader, clearing out any pending data
    }
  }

  async readData(onDataReceived) {
    if (!this.isOpen || !this.reader) {
      throw new Error('Port is not open or reader is not available');
    }
  
    const textDecoder = new TextDecoder();
    let buffer = '';
  
    // Enqueue the read operation
    this.readQueue = this.readQueue.then(async () => {
      try {
        while (!this.isStopping) {
          const { value, done } = await this.reader.read();
          if (done) {
            console.log('Reader done, breaking loop');
            break;
          }
  
          if (value) {
            buffer += textDecoder.decode(value, { stream: true });
            
            let lineEndIndex;
            while ((lineEndIndex = buffer.indexOf('\n')) !== -1) {
              const line = buffer.slice(0, lineEndIndex).trim();
              if (line) {
      
                onDataReceived(line);
              }
              buffer = buffer.slice(lineEndIndex + 1);
            }
          }
  
          // Check for stop condition after processing each chunk
          if (this.isStopping) {
            console.log('Stop requested, exiting read loop');
            break;
          }
        }
  
        // Process any remaining data in the buffer
        if (buffer.trim()) {
          console.log('Remaining Data:', buffer.trim());
          onDataReceived(buffer.trim());
        }
      } catch (error) {
        if (this.isStopping) {
          console.log('Reading stopped due to stop request');
        } else {
          console.error('Error in readData:', error);
          throw error;
        }
      } finally {
        // Reset the stopping flag
        this.isStopping = false;
      }
    });
  
    // Wait for the read operation to complete
    await this.readQueue;
  }


  async stopMeasurement() {
    console.log('Stopping measurement...');
    this.isStopping = true;
    this.isReading = false;

    if (this.reader) {
      try {
        await this.reader.cancel();
      } catch (error) {
        console.warn('Error cancelling reader:', error);
      }
    }

    try {
      await this.writeData(new TextEncoder().encode('Q\r\n'));
    } catch (error) {
      console.warn('Error sending stop command:', error);
    }

    // Reset the read queue
    this.readQueue = Promise.resolve();

    console.log('Measurement stopped');
  }




  async requestUserPermission() {
    console.log('Requesting user permission for serial port access...');
    if (!('serial' in navigator)) {
      console.error('Web Serial API is not supported in this browser');
      return false;
    }
    try {
      const port = await navigator.serial.requestPort();
      console.log('User granted permission, port:', port);
      this.port = port;
      return true;
    } catch (error) {
      console.error('User denied permission or no ports available:', error);
      return false;
    }
  }

  async listPorts() {
    console.log('listPorts method called');
    if (!('serial' in navigator)) {
      console.error('Web Serial API is not supported in this browser');
      return [];
    }

    try {
      console.log('Attempting to get existing ports...');
      const existingPorts = await navigator.serial.getPorts();
      console.log('Existing ports:', existingPorts);

      const portInfoList = existingPorts.map(port => {
        const info = port.getInfo();
        return {
          usbVendorId: info.usbVendorId,
          usbProductId: info.usbProductId
        };
      });

      console.log('Processed port info list:', portInfoList);
      return portInfoList;
    } catch (error) {
      console.error('Error in listPorts:', error);
      return [];
    }
  }

  async requestPortAndList() {
    console.log('Requesting port and listing available ports...');
    const userPermissionGranted = await this.requestUserPermission();
    if (!userPermissionGranted) {
      console.error('User did not grant permission for port access');
      return [];
    }
    const ports = await this.listPorts();
    console.log('Available ports:', ports);
    return ports;
  }

  setBaudRate(rate) {
    console.log(`Setting baud rate to ${rate}`);
    this.baudRate = rate;
  }

  async stopReading() {
    if (this.reader) {
      await this.reader.cancel();
      this.reader = null;
    }
    console.log('Reading stopped');
  }

  async selectPort(portInfo) {
    console.log('Selecting port with info:', portInfo);
    const ports = await navigator.serial.getPorts();
    console.log('Available ports:', ports);
    const selectedPort = ports.find(p => 
      p.getInfo().usbVendorId === portInfo.usbVendorId && 
      p.getInfo().usbProductId === portInfo.usbProductId
    );
    if (!selectedPort) {
      console.error('Selected port not found');
      throw new Error('Selected port not found');
    }
    console.log('Port selected:', selectedPort);
    this.port = selectedPort;
    return selectedPort;
  }
}

const sensorService = new SensorService();
export default sensorService;